import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import { StateCard, HospitalCard } from "@/components/common/Cards";
import { SourceBadge } from "@/components/common/Badges";

const ORGANS = ["kidney", "liver", "heart", "lung", "pancreas", "intestine"];
const ORGAN_LABELS = { kidney: "Kidney", liver: "Liver", heart: "Heart", lung: "Lung", pancreas: "Pancreas", intestine: "Intestine" };

export default function Directory() {
  const [states, setStates] = useState([]);
  const [centres, setCentres] = useState([]);
  const [organ, setOrgan] = useState("");
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (organ) params.set("organ", organ);
    if (stateFilter) params.set("state", stateFilter);
    if (q.trim()) params.set("q", q.trim());
    const t = setTimeout(() => {
      api.get(`/centres?${params.toString()}`).then((r) => setCentres(r.data));
    }, 250);
    return () => clearTimeout(t);
  }, [organ, stateFilter, q]);

  const searching = !!(q.trim() || organ || stateFilter);
  const stateName = states.find((s) => s.code === stateFilter)?.name || "";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title="Transplant Centers in India — Transplant Story" description="A national directory of transplant centers across all Indian states and Union Territories, labelled by source." />
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-ink">Transplant centers</h1>
      <p className="text-lg text-neutral-600 mt-4 max-w-2xl">A national directory across every state and Union Territory. We list what is there — we do not rank or recommend.</p>
      {stats && <div className="mt-5"><SourceBadge source={stats.official_source.source} updated={stats.official_source.updated} /></div>}

      <div className="flex flex-col sm:flex-row gap-3 mt-10">
        <div className="flex items-center border border-neutral-300 rounded-full px-4 py-2.5 bg-white focus-within:border-rust transition-colors sm:w-80">
          <Search className="w-4 h-4 text-neutral-400 shrink-0" />
          <input
            data-testid="centres-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search hospital or city"
            className="bg-transparent outline-none text-sm ml-2 w-full placeholder:text-neutral-400"
          />
        </div>
        <select
          data-testid="centres-state-filter"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="px-4 py-2.5 text-sm border border-neutral-300 rounded-full bg-white text-ink outline-none focus:border-rust transition-colors"
        >
          <option value="">All states</option>
          {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Pill active={!organ} onClick={() => setOrgan("")}>All organs</Pill>
        {ORGANS.map((o) => <Pill key={o} active={organ === o} onClick={() => setOrgan(o)} testId={`dir-organ-${o}`}>{ORGAN_LABELS[o]}</Pill>)}
      </div>

      {!searching && (
        <>
          <h2 className="text-xl font-medium text-ink mt-14 mb-5">Browse by state</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {states.map((s) => <StateCard key={s.code} state={s} />)}
          </div>
        </>
      )}

      <h2 className="text-xl font-medium text-ink mt-10 mb-5" data-testid="dir-results-heading">
        {q.trim() ? `Results for "${q.trim()}"` : organ ? `${ORGAN_LABELS[organ]} centers` : stateFilter ? `${stateName} centers` : "All centers"}{" "}
        <span className="font-mono text-sm text-neutral-400">({centres.length})</span>
      </h2>
      {searching && centres.length === 0 ? (
        <p data-testid="dir-no-results" className="text-neutral-500 border border-dashed border-neutral-300 p-10 text-center">
          No centers match your search. Try a hospital name, a city, or clear the filters.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {centres.map((c) => <HospitalCard key={c.id} centre={c} />)}
        </div>
      )}
    </div>
  );
}

function Pill({ active, onClick, children, testId }) {
  return (
    <button data-testid={testId} onClick={onClick}
      className={`px-4 py-2 text-sm rounded-full border transition-colors ${active ? "bg-rust text-white border-rust" : "border-neutral-300 text-neutral-600 hover:border-ink"}`}>
      {children}
    </button>
  );
}
